import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import ContentSaveOutlineIcon from 'mdi-react/ContentSaveOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

export const UnsavedChangesButton: FC<{
  isLoading: boolean
  hasUnsavedChanges: boolean
  error?: boolean
  disabled?: boolean
  onClick?: () => void
}> = ({ error, hasUnsavedChanges, isLoading, disabled, onClick }) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const content = error
    ? translate('Oops! Resolve form errors first.')
    : hasUnsavedChanges
      ? translate("NB! You've got unsaved changes.")
      : ''

  return (
    <span id="unsaved-changes">
      <Button
        variant="primary"
        className={twJoin(
          hasUnsavedChanges && !isLoading && 'transition duration-500 ease-in-out',
          hasUnsavedChanges &&
            !isLoading &&
            (buttonUnsavedStyles[whiteLabel] || buttonUnsavedStyles['skytech'])
        )}
        disabled={disabled || error}
        type="submit"
        Icon={<ContentSaveOutlineIcon />}
        isLoading={isLoading}
        onClick={onClick}
      >
        <Translation id="Save changes" />
        <Tooltip anchorId="unsaved-changes" size="large" content={content} />
      </Button>
    </span>
  )
}

const buttonUnsavedStyles: Partial<Record<WhiteLabel, string>> = {
  skytech:
    'bg-yellow-500 text-gray-900 brightness-100 hover:bg-yellow-500/50 focus:bg-yellow-500/50 active:bg-yellow-500/50',
  ice: 'bg-[#ffb901] text-gray-900 brightness-100 hover:bg-[#ffb901]/50 focus:bg-[#ffb901]/50 active:bg-[#ffb901]/50',
  threeStepIT:
    'bg-threeStepIT-warning brightness-100 hover:bg-threeStepIT-warning/50 focus:bg-threeStepIT-warning/50 active:bg-threeStepIT-warning/50',
  komplett:
    'bg-komplett-lightYellow brightness-100 text-komplett hover:bg-komplett-lightYellow/50 focus:bg-komplett-lightYellow/50 active:bg-komplett-lightYellow/50',
}
